import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine } from '@remixicon/react';
import { ModalHeader, ModalBody, ModalFooter } from '@/design-system/modal';
import { cn } from '@/design-system/lib/utils';

/**
 * Open modals, bottom first. Escape belongs to the top one only, so a modal
 * opened over another (see `stacked`) closes alone and the one under it keeps
 * its state until it is on top again.
 */
const openModals: object[] = [];

export interface SideBarModalProps {
  isOpen: boolean;
  onClose: () => void;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  position?: 'center' | 'right';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  /** Lock body scroll while open. Default true. */
  lockBodyScroll?: boolean;
  /**
   * Show an "Are you sure?" prompt before closing. Useful for forms with
   * unsaved data. Off by default; opt-in when needed.
   */
  confirmOnClose?: boolean;
  maxHeight?: 'screen' | 'auto';
  className?: string;
  contentRef?: React.Ref<HTMLDivElement>;
  /**
   * Opened on top of another modal: every layer sits above a regular modal's
   * (scrim z-40, dialog z-50, exit confirm z-60/70), so the modal underneath
   * is dimmed by this one's scrim.
   */
  stacked?: boolean;
}

const SideBarModal: React.FC<SideBarModalProps> = ({
  isOpen,
  onClose,
  header,
  children,
  footer,
  width = 'md',
  position = 'center',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  lockBodyScroll = true,
  confirmOnClose = false,
  maxHeight = 'screen',
  className = '',
  contentRef,
  stacked = false,
}) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const layer = useRef<object>({});

  useEffect(() => {
    if (isOpen) {
      setShowExitConfirm(false);
    }
  }, [isOpen]);

  // Take a place in the stack while open — even with Escape disabled (a save
  // in flight), so the modals below stay put too.
  useEffect(() => {
    if (!isOpen) return;
    const mine = layer.current;
    openModals.push(mine);
    return () => {
      openModals.splice(openModals.indexOf(mine), 1);
    };
  }, [isOpen]);

  // Lock page scroll while open.
  //
  // Locking `body` alone does nothing here: the app lays out full-height and
  // scrolls inside its own container, so `body` never overflows and the page
  // behind the modal kept scrolling. Lock whichever element is actually
  // scrolling too, and restore exactly what was there before.
  useEffect(() => {
    if (!isOpen || !lockBodyScroll) return;
    const locked: { el: HTMLElement; previous: string }[] = [];
    const lock = (el: HTMLElement | null) => {
      if (!el) return;
      locked.push({ el, previous: el.style.overflow });
      el.style.overflow = 'hidden';
    };

    lock(document.body);
    // Every real scroller outside the dialog — anything taller than its own box
    // with a scrolling overflow. All of them, not the first one found: the
    // shell has several (sidebar, page, sticky panes) and the one that matters
    // is rarely the earliest in document order.
    Array.from(document.querySelectorAll<HTMLElement>('body *'))
      .filter(
        (el) =>
          el.scrollHeight > el.clientHeight + 1 &&
          /auto|scroll/.test(getComputedStyle(el).overflowY) &&
          !el.closest('[role="dialog"]')
      )
      .forEach(lock);

    return () => {
      for (const { el, previous } of locked) el.style.overflow = previous;
    };
  }, [isOpen, lockBodyScroll]);

  // Escape closes (or opens the confirm dialog if confirmOnClose is on).
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A popover / select inside the modal already took this Escape (Radix
      // marks it handled): it closes alone, the modal stays.
      if (e.defaultPrevented) return;
      if (openModals[openModals.length - 1] !== layer.current) return;
      if (showExitConfirm) {
        setShowExitConfirm(false);
        return;
      }
      if (confirmOnClose) {
        setShowExitConfirm(true);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEscape, confirmOnClose, onClose, showExitConfirm]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const widthClasses = {
    sm: 'w-[400px]',
    md: 'w-[500px]',
    lg: 'w-[600px]',
    xl: 'w-[800px]',
    full: 'w-full max-w-7xl',
  };

  const positionClasses = {
    center: 'items-center justify-center p-4',
    right: 'justify-end items-stretch',
  };

  const modalClasses = {
    center: cn(
      widthClasses[width],
      'max-h-[90vh] bg-bg-white-0 rounded-2xl border border-stroke-soft-200 shadow-custom-shadows-large flex flex-col'
    ),
    right: cn(
      widthClasses[width],
      'h-full bg-bg-white-0 border-l border-stroke-soft-200 shadow-custom-shadows-large flex flex-col'
    ),
  };

  const handleRequestClose = () => {
    if (confirmOnClose) {
      setShowExitConfirm(true);
      return;
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!closeOnBackdropClick || e.target !== e.currentTarget) return;
    handleRequestClose();
  };

  return createPortal(
    <>
      {/* Backdrop */}
      {/* `transform-gpu` keeps the blur on its own compositor layer. Without it
          Chrome re-rasterizes the whole backdrop every time anything behind it
          moves, which reads as a flicker while the modal body scrolls. */}
      <div
        className={`fixed inset-0 bg-[#171717]/45 backdrop-blur-sm ${stacked ? 'z-[80]' : 'z-40'} transition-opacity !mt-0 transform-gpu`}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed inset-0 ${stacked ? 'z-[90]' : 'z-50'} flex ${positionClasses[position]}`}
        onClick={handleBackdropClick}
      >
        <div className={cn('relative', modalClasses[position], className)}>
          {/* Header */}
          {(header || showCloseButton) && (
            <ModalHeader className="flex-row items-start justify-between gap-3 px-5 py-4 border-b border-stroke-soft-200 shrink-0">
              <div className="flex-1 min-w-0">{header}</div>
              {showCloseButton && (
                <button
                  type="button"
                  onClick={handleRequestClose}
                  className="-mr-1 -mt-0.5 shrink-0 size-7 inline-flex items-center justify-center rounded-md text-icon-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-strong-950 transition-colors"
                  aria-label="Close modal"
                >
                  <RiCloseLine className="size-5" aria-hidden />
                </button>
              )}
            </ModalHeader>
          )}

          {/* Content */}
          <ModalBody className="p-0 overflow-hidden flex flex-col">
            {/* `overscroll-contain`: once this list bottoms out the wheel must
                stop here. Chaining would scroll the page underneath — which the
                blurred backdrop then has to re-sample, so the flicker showed up
                exactly at the ends of the scroll. */}
            <div
              ref={contentRef}
              className={cn(
                'flex-1 overflow-y-auto overscroll-contain',
                maxHeight === 'screen' && 'max-h-full'
              )}
            >
              {children}
            </div>
          </ModalBody>

          {/* Footer */}
          {footer && (
            <ModalFooter className="block bg-bg-weak-50 px-5 py-3 shrink-0">
              {footer}
            </ModalFooter>
          )}
        </div>
      </div>

      {showExitConfirm && (
        <>
          <div
            className={`fixed inset-0 bg-[#171717]/45 backdrop-blur-sm ${stacked ? 'z-[100]' : 'z-[60]'}`}
          />
          <div
            className={`fixed inset-0 ${stacked ? 'z-[110]' : 'z-[70]'} flex items-center justify-center p-4`}
          >
            <div className="bg-bg-white-0 rounded-2xl border border-stroke-soft-200 shadow-custom-shadows-large max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0">
                    <i className="ri-alert-line text-(--state-warning-base) text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium tracking-tight text-text-strong-950">
                      Discard unsaved changes?
                    </h3>
                    <p className="mt-1 text-sm text-text-sub-600">
                      You have entered details into the form that are not yet
                      saved. Closing the form now will permanently lose them.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowExitConfirm(false);
                      onClose();
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium tracking-tight text-text-sub-600 bg-bg-white-0 border border-stroke-soft-200 rounded-lg hover:bg-bg-weak-50 hover:text-text-strong-950 focus:outline-none focus:ring-2 focus:ring-stroke-sub-300 transition-colors"
                  >
                    Discard and exit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowExitConfirm(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium tracking-tight text-text-white-0 border border-transparent rounded-lg bg-bg-strong-950 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stroke-strong-950 transition-opacity"
                  >
                    Stay and continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
};

export default SideBarModal;
